import { ConversationStatus } from '@provision/conversation-types';
import { memo, useMemo, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { DynamicIcon, ShortcutText } from 'src/components';
import { SelectMenu, SelectMenuOption } from 'src/components/SelectMenu';
import { appShortcuts } from 'src/constants/appShortcuts';
import {
  ConversationStatusOption,
  useConversationStatusOptions,
  useGroupRules,
  UserGroupRules,
} from 'src/graphql';
import { useRegisterShortcuts } from 'src/utils/hooks';

const RESTRICTED_STATUSES = [
  ConversationStatus.Closed,
  ConversationStatus.Snoozed,
  ConversationStatus.New,
];

const ConversationStatusToggleComponent = ({
  name,
  disabled,
}: {
  name: string;
  disabled?: boolean;
}) => {
  const { control } = useFormContext();
  const triggerRef = useRef<HTMLButtonElement>(null);

  const { groupRules } = useGroupRules();
  const canUpdateAllConversationStatus =
    groupRules[UserGroupRules.AccessToUpdateAllConversationStatuses] === true;
  const canUpdateStatus =
    groupRules[UserGroupRules.AccessToUpdateConversationStatus] === true ||
    canUpdateAllConversationStatus;
  const isDisabled = Boolean(disabled) || !canUpdateStatus;

  useRegisterShortcuts(
    useMemo(
      () => ({
        ...appShortcuts.changeStatus,
        callback: () => {
          if (isDisabled || document.activeElement === triggerRef.current) {
            return false;
          }
          triggerRef.current?.focus();
          triggerRef.current?.click();
          return false;
        },
      }),
      [isDisabled],
    ),
  );

  const statusOptions = useConversationStatusOptions();
  const allOptions = useMemo<SelectMenuOption[]>(
    () =>
      statusOptions.map((option) => ({
        value: option.id,
        title: option.label,
        icon: <DynamicIcon weight={500} name={option.icon} />,
        iconColor: option.color,
      })),
    [statusOptions],
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange } }) => {
        const selectedId = (value as ConversationStatusOption | null)?.id ?? null;
        const options = allOptions.filter(
          (option) =>
            canUpdateAllConversationStatus ||
            option.value === selectedId ||
            !RESTRICTED_STATUSES.some(
              (status) => status === (option.value as ConversationStatus),
            ),
        );

        return (
          <SelectMenu
            ref={triggerRef}
            label="Status"
            options={options}
            value={selectedId}
            onChange={(selected) =>
              onChange(statusOptions.find((option) => option.id === selected))
            }
            search
            hotkey="S"
            variant="inline"
            plainOptionIcons
            disabled={isDisabled}
            sx={{ width: '100%' }}
            tooltip={
              <ShortcutText
                label={appShortcuts.changeStatus.description}
                shortcut={appShortcuts.changeStatus.command}
                size="small"
              />
            }
          />
        );
      }}
    />
  );
};

export const ConversationStatusToggle = memo(ConversationStatusToggleComponent);
